import ExecutionMode from 'core/base/execution-mode';
import deviceTypeGuesser from 'core/base/device-type-guesser';
import Build from 'core/generated/build.js';
import SystemSettingsTypes from 'core/services/system-settings/types.js';
/**
 * Generic core-level utility functions.
 *
 * @class CoreUtils
 * @memberof Mojito.Core
 */

const { APPLICATION_MODE, CHANNEL } = SystemSettingsTypes;
const APP_MODE_TO_CHANNEL = {
    [APPLICATION_MODE.MOBILE]: CHANNEL.MOBILE,
    [APPLICATION_MODE.DESKTOP]: CHANNEL.DESKTOP,
};

export default {
    /**
     * Best-guess {@link Mojito.Core.Services.SystemSettings.types.APPLICATION_MODE|application mode}
     * from device characteristics when it is not configured explicitly.
     *
     * @returns {Mojito.Core.Services.SystemSettings.types.APPLICATION_MODE} `mobile` or `desktop`.
     * @function Mojito.Core.CoreUtils#bestGuessApplicationMode
     */
    bestGuessApplicationMode: () =>
        deviceTypeGuesser.isProbablyHandHeldDevice()
            ? APPLICATION_MODE.MOBILE
            : APPLICATION_MODE.DESKTOP,

    /**
     * The version of mojito application.
     *
     * @returns {string} Mojito version.
     * @function Mojito.Core.CoreUtils#appVersion
     */
    appVersion: () => Build.appVersion,

    /**
     * Git commit SHA of mojito sources.
     *
     * @returns {string} Commit SHA.
     * @function Mojito.Core.CoreUtils#gitVersion
     */
    gitVersion: () => Build.gitVersion,

    /**
     * Returns true if currently executing in debug mode.
     *
     * @returns {boolean} True if debug mode is activated, otherwise false.
     *
     * @function Mojito.Core.CoreUtils#isDebugMode
     */
    isDebugMode: () => ExecutionMode.isDebugMode(),

    /**
     * Redirects to the specified URL.
     *
     * @param {string} url - URL to redirect to.
     * @param {string} windowName - Window name.
     * @param {string} windowFeatures - Window features.
     * @function Mojito.Core.CoreUtils#openUrl
     */
    openUrl: (url, windowName, windowFeatures) => {
        if (url) {
            if (windowName === undefined) {
                window.location.assign(url);
            } else {
                window.open(url, windowName, windowFeatures);
            }
        }
    },

    /**
     * Generates a universally unique identifier (UUID) in vanilla javascript. If browser supports it, use window.crypto.randomUUID()
     * function, otherwise generate alphanumeric string based using Math.random() function.
     *
     * @returns {string} Generated UUID.
     * @function Mojito.Core.CoreUtils#generateUUID
     */
    generateUUID: () => {
        if (window.crypto && window.crypto.randomUUID) {
            return window.crypto.randomUUID();
        }
        const s4 = () => {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };
        // return id of format 'aaaaaaaa'-'aaaa'-'aaaa'-'aaaa'-'aaaaaaaaaaaa'
        return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
    },

    /**
     * Resolves communication channel by specific application mode.
     * Typically used within Mojito API calls.
     *
     * @param {Mojito.Core.Services.SystemSettings.type.APPLICATION_MODE} mode - Application mode.
     *
     * @returns {Mojito.Services.Common.types.CHANNEL} Channel.
     * @function Mojito.Services.Common.utils.resolveChannel
     */
    resolveChannel: mode => APP_MODE_TO_CHANNEL[mode],

    /**
     * Returns promise that resolves in certain amount of ms.
     *
     * @param {number} [ms = 0] - Amount of milliseconds to wait.
     *
     * @returns {Promise} Promise.
     * @function Mojito.Services.Common.utils.waitFor
     */
    waitFor: (ms = 0) => new Promise(resolve => setTimeout(resolve, ms)),
};
